/** View as a chair: read-only, one hour, audited and visible to the chair. The picker lists this year's chairs with an area (People Part 2). */
import { useEffect, useState, type ReactNode } from 'react';
import { Button, Field, Form, Input } from '@cider/ui';
import { api, sentence } from './api.js';
import { areaWords } from './words.js';
import { detroitYear } from './year.js';

interface ChairRow {
  id: string;
  personId: string;
  name: string;
  roleName: string;
  areaId: string | null;
  areaName?: string | null;
  state: string;
}

export function ViewAs({
  onDone,
  onCancel,
}: {
  onDone: () => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const [chairPersonId, setChair] = useState('');
  const [areaId, setArea] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [chairs, setChairs] = useState<ChairRow[] | null>(null);
  useEffect(() => {
    const year = detroitYear();
    api<ChairRow[]>('GET', `/admin/roles/year?year=${String(year)}`)
      .then((rows) => {
        setChairs(rows.filter((r) => r.areaId && (r.state === 'active' || r.state === 'granted')));
      })
      .catch(() => {
        setChairs([]);
      });
  }, []);
  return (
    <div className="ui-dialog" role="dialog" aria-modal="true" aria-label="View as a chair">
      <div className="ui-dialog__card">
        <h1>View as a chair</h1>
        <p className="charter">Read-only for one hour. The chair sees that you did.</p>
        <Form
          busy={busy}
          refusal={refusal}
          onSubmit={() => {
            setBusy(true);
            setRefusal(null);
            api('POST', '/admin/view-as', { chairPersonId, areaId })
              .then(onDone)
              .catch((e: unknown) => {
                setRefusal(sentence(e));
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          {chairs && chairs.length > 0 ? (
            <Field id="chair-pick" label="The chair">
              <select
                id="chair-pick"
                className="ui-input"
                value={
                  chairs.find((c) => c.personId === chairPersonId && c.areaId === areaId)?.id ?? ''
                }
                onChange={(e) => {
                  const c = chairs.find((x) => x.id === e.target.value);
                  setChair(c?.personId ?? '');
                  setArea(c?.areaId ?? '');
                }}
              >
                <option value="">— pick a chair —</option>
                {chairs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} — {c.roleName} · {areaWords(c.areaName, c.areaId)}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <>
              <p>
                {chairs === null
                  ? 'Looking for this year’s chairs…'
                  : 'No chair with an area this year; the two ids by hand:'}
              </p>
              <Field id="chair" label="The chair’s person id">
                <Input
                  id="chair"
                  required
                  value={chairPersonId}
                  onChange={(e) => {
                    setChair(e.target.value);
                  }}
                />
              </Field>
              <Field id="area" label="Their area id">
                <Input
                  id="area"
                  required
                  value={areaId}
                  onChange={(e) => {
                    setArea(e.target.value);
                  }}
                />
              </Field>
            </>
          )}
          <div className="ui-row">
            <Button kind="primary" type="submit" busy={busy}>
              View as them
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
